<template>
  <WCWebAdsCarouselModern
    class="wc-web-ads wc-carousel--negate-container position-relative"
    :isSlider="true"
  >
    <Splide :options="primaryOptions" ref="primary">
      <slot></slot>
    </Splide>
    <Splide
      :options="secondaryOptions"
      class="wc-splide m-auto rounded"
      ref="secondary"
      :has-track="false"
    >
      <SplideTrack>
        <slot> </slot>
      </SplideTrack>
      <div class="wc-autoplay-wrapper">
        <button class="splide__toggle wc-autoplay" type="button">
          <span class="splide__toggle__pause">
            <font-awesome-icon
              icon="pause"
              data-testid="pauseButton"
              :aria-label="t('pauseAutoplay')"
            />
          </span>
          <span class="splide__toggle__play">
            <font-awesome-icon
              icon="play"
              data-testid="playButton"
              :aria-label="t('startAutoplay')"
            />
          </span>
        </button>
      </div>
    </Splide>
  </WCWebAdsCarouselModern>
</template>

<script setup lang="ts">
import { onMounted, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { Splide, SplideTrack } from '@splidejs/vue-splide';
import type { Options } from '@splidejs/splide';
import WCWebAdsCarouselModern from '../WCWebAdsCarouselModern/WCWebAdsCarouselModern.vue';

const { t } = useI18n();

const primaryOptions: Options = {
  perPage: 1,
  arrows: false,
  pauseOnHover: false,
  pauseOnFocus: false,
  rewind: true,
  preloadPages: 3,
  lazyLoad: 'nearby',
  gap: '0.2rem',
  type: 'loop',
  keyboard: 'global',
  pagination: true,
  paginationKeyboard: true,
  paginationDirection: 'ltr',
};

const secondaryOptions: Options = {
  type: 'slide',
  arrows: false,
  rewind: true,
  gap: '0.2rem',
  pagination: false,
  autoplay: true,
  fixedWidth: 1,
  fixedHeight: 1,
  focus: 'center',
  preloadPages: 3,
  lazyLoad: 'nearby',
  isNavigation: true,
  updateOnMove: true,
  pauseOnHover: false,
  pauseOnFocus: false,
};

const primaryRef = useTemplateRef<InstanceType<typeof Splide>>('primary');
const secondaryRef = useTemplateRef<InstanceType<typeof Splide>>('secondary');

onMounted(() => {
  const primary = primaryRef.value;
  const secondary = secondaryRef.value;

  if (!primary || !secondary || !secondary.splide) {
    return;
  }

  primary.sync(secondary.splide);

  const secondaryTrack = secondary.splide.Components.Elements.track;
  secondaryTrack.classList.add('d-none');
});
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-splide {
  position: absolute;
  width: 100%;
  bottom: -0.3rem;
}

:deep(.splide__pagination__page) {
  width: 0.625rem;
  height: 0.625rem;
}

@include media-breakpoint-up(lg) {
  .wc-splide {
    width: 100%;
  }
}

@include media-breakpoint-down(sm) {
  :deep(.splide__pagination__page) {
    width: 0.9375rem !important;
    height: 0.9375rem !important;
    margin-left: 0.625rem !important;
    margin-right: 0.625rem !important;
  }
}

:deep(.splide__pagination) {
  flex-wrap: nowrap;
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
  bottom: 0.7em !important;
}

.wc-autoplay {
  position: absolute;
  bottom: 0.625rem;
  right: 0.75rem;
  width: 1.625rem;
  height: 1.625rem;
  border-radius: 0.8125rem;
  border: 0.0625rem solid var(--secondary-lighter-2);
  background: var(--secondary-lighter-8);
  color: var(--primary);
  font-size: 0.625rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.08);
}

.wc-autoplay-wrapper {
  width: 100%;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
}

.splide__slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

:deep(.splide__pagination__page:focus-visible) {
  outline: none !important;
}

:deep(.splide.is-focus-in .splide__pagination__page:focus) {
  outline: none !important;
}

:deep(.splide__arrow:focus-visible) {
  outline: none !important;
}

:deep(.splide.is-focus-in .splide__arrow:focus) {
  outline: none !important;
}

.wc-web-ads {
  :deep(.splide__toggle__play .svg-inline--fa),
  :deep(.splide__toggle__pause .svg-inline--fa) {
    font-size: 1.25rem;
    display: flex;
    position: relative;
    right: -0.01875rem;
    color: $splide-toggle-pause-play-button !important;
  }
}
</style>
